import { Controller } from '@hotwired/stimulus'

const DWELL_TIME_MS = 1000
const FLUSH_INTERVAL_MS = 5000
const MAX_BATCH_SIZE = 50

const SEEN_TTL_MS = 30 * 60 * 1000
const seenInSession = new Map()

function wasSeen(postId) {
  const expiresAt = seenInSession.get(postId)
  if (expiresAt && expiresAt > Date.now()) return true
  seenInSession.delete(postId)
  return false
}
const latestCounts = new Map()

function displayCount(metric) {
  document.querySelectorAll(`.impression_count_${CSS.escape(metric.number)}`).forEach((element) => {
    const current = Number(element.textContent.replace(/,/g, '')) || 0
    element.textContent = String(Math.max(current, metric.count))
  })
}
const pendingPostIds = new Set()
let flushTimer = null

function getCsrfToken() {
  const meta = document.querySelector('meta[name="csrf-token"]')
  return meta ? meta.getAttribute('content') : ''
}

let inFlight = false
let retryDelay = FLUSH_INTERVAL_MS

function scheduleFlush(delay = FLUSH_INTERVAL_MS) {
  if (!flushTimer && pendingPostIds.size > 0) {
    flushTimer = setTimeout(() => {
      flushTimer = null
      flushPendingImpressions()
    }, delay)
  }
}

async function flushPendingImpressions() {
  if (inFlight || pendingPostIds.size === 0) return
  const postIds = Array.from(pendingPostIds).slice(0, MAX_BATCH_SIZE)
  if (flushTimer) {
    clearTimeout(flushTimer)
    flushTimer = null
  }
  inFlight = true
  try {
    const response = await fetch('/posts/batch_impressions', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json', 'X-CSRF-Token': getCsrfToken() },
      body: JSON.stringify({ post_ids: postIds }),
      keepalive: true
    })
    if (!response.ok) throw new Error(`Tracking failed: ${response.status}`)
    const data = await response.json()
    for (const metric of data.counts || []) {
      latestCounts.set(String(metric.id), metric)
      displayCount(metric)
    }
    for (const [id, expiresAt] of seenInSession) {
      if (expiresAt <= Date.now()) { seenInSession.delete(id); latestCounts.delete(id) }
    }
    postIds.forEach((id) => {
      pendingPostIds.delete(id)
      seenInSession.set(id, Date.now() + SEEN_TTL_MS)
    })
    retryDelay = FLUSH_INTERVAL_MS
  } catch (error) {
    scheduleFlush(retryDelay)
    retryDelay = Math.min(retryDelay * 2, 60000)
  } finally {
    inFlight = false
    scheduleFlush()
  }
}

function queuePostImpression(postId) {
  if (!postId || wasSeen(postId) || pendingPostIds.has(postId)) return
  pendingPostIds.add(postId)
  scheduleFlush()
}

export default class extends Controller {
  static values = {
    id: String
  }

  connect() {
    if (!this.hasIdValue || !this.idValue) return

    this.postId = this.idValue
    this.dwellTimer = null
    const savedCount = latestCounts.get(this.postId)
    if (savedCount) displayCount(savedCount)

    this.onVisibilityChange = () => {
      this.cancelDwellTimer()
      if (!document.hidden && this.isVisible) this.startDwellTimer()
    }
    document.addEventListener('visibilitychange', this.onVisibilityChange)
    this.setupObserver()
  }

  disconnect() {
    document.removeEventListener('visibilitychange', this.onVisibilityChange)
    this.cancelDwellTimer()
    if (this.observer) {
      this.observer.disconnect()
      this.observer = null
    }
  }

  setupObserver() {
    if (wasSeen(this.postId)) return

    if ('IntersectionObserver' in window) {
      this.observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            const viewport = entry.rootBounds
            const card = entry.boundingClientRect
            const visible = entry.intersectionRect
            this.isVisible = entry.isIntersecting && viewport &&
              visible.height >= Math.min(card.height, viewport.height) * 0.5 &&
              visible.width >= Math.min(card.width, viewport.width) * 0.5
            if (this.isVisible && !document.hidden) {
              this.startDwellTimer()
            } else {
              this.cancelDwellTimer()
            }
          })
        },
        {
          threshold: Array.from({ length: 101 }, (_, index) => index / 100)
        }
      )
      this.observer.observe(this.element)
    }
  }

  startDwellTimer() {
    if (document.hidden || this.dwellTimer || wasSeen(this.postId)) return

    this.dwellTimer = setTimeout(() => {
      this.dwellTimer = null
      queuePostImpression(this.postId)

      if (this.observer) {
        this.observer.disconnect()
        this.observer = null
      }
    }, DWELL_TIME_MS)
  }

  cancelDwellTimer() {
    if (this.dwellTimer) {
      clearTimeout(this.dwellTimer)
      this.dwellTimer = null
    }
  }
}

window.addEventListener('pagehide', () => flushPendingImpressions())
window.addEventListener('online', () => flushPendingImpressions())
document.addEventListener('visibilitychange', () => {
  if (document.hidden) flushPendingImpressions()
})
